// Zustand stores for Digitale Feuerwache
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import * as perms from './permissions';

// Auth Store
export const useAuthStore = create(
  persist(
    (set, get) => ({
      user: null,
      effectivePermissions: [],
      isAuthenticated: false,
      isLoading: true,
      
      setUser: (user) => set({ user, isAuthenticated: !!user, isLoading: false }),
      setEffectivePermissions: (permissions) => set({ effectivePermissions: permissions || [] }),
      clearUser: () => {
        // Remove legacy token entries left by versions before cookie-only auth.
        try {
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
        } catch (e) { /* ignore */ }
        set({ user: null, effectivePermissions: [], isAuthenticated: false, isLoading: false });
      },
      setLoading: (isLoading) => set({ isLoading }),
      
      // Role checks
      isDeveloper: () => get().user?.rolle === 'DEVELOPER',
      isWachleitung: () => ['DEVELOPER', 'WACHLEITUNG', 'WACHLEITUNG_VERTRETUNG'].includes(get().user?.rolle),
      isAbteilungsleitung: () => ['DEVELOPER', 'WACHLEITUNG', 'WACHLEITUNG_VERTRETUNG', 'WACHABTEILUNGSLEITUNG'].includes(get().user?.rolle),
      isAdmin: () => ['DEVELOPER', 'WACHLEITUNG', 'WACHLEITUNG_VERTRETUNG', 'WACHABTEILUNGSLEITUNG', 'ADMIN'].includes(get().user?.rolle),
      
      // Permission checks (granular)
      hasPermission: (perm) => perms.hasPermission(get().user, perm),
      hasEffectivePermission: (permissionKey) => (
        get().user?.rolle === 'DEVELOPER'
        || get().effectivePermissions.includes(permissionKey)
      ),
      isGlobalRole: () => perms.isGlobalRole(get().user?.rolle),
      canEditEinteilung: (abteilung_id) => perms.canEditEinteilung(get().user, abteilung_id),
      canEditUser: (rolle, abt) => perms.canEditUser(get().user, rolle, abt),
      canDeleteUser: (rolle, abt) => perms.canDeleteUser(get().user, rolle, abt),
      canResetPassword: (rolle, abt) => perms.canResetPassword(get().user, rolle, abt),
      canSetZusatzberechtigungen: () => perms.canSetZusatzberechtigungen(get().user),
      canSetTags: () => perms.canSetTags(get().user),
      canCreateFahrzeug: () => perms.canCreateFahrzeug(get().user),
      canEditFahrzeug: () => perms.canEditFahrzeug(get().user),
      
      // Get user info
      getFullName: () => {
        const user = get().user;
        return user ? `${user.vorname} ${user.nachname}` : '';
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({ user: state.user, isAuthenticated: state.isAuthenticated }),
    }
  )
);

// Wache Config Store
export const useWacheStore = create(
  persist(
    (set) => ({
      wache: null,
      abteilungen: [],
      
      setWache: (wache) => set({ wache }),
      setAbteilungen: (abteilungen) => set({ abteilungen }),
      clear: () => set({ wache: null, abteilungen: [] }),
    }),
    {
      name: 'wache-storage',
    }
  )
);

// UI Store
export const useUIStore = create((set) => ({
  sidebarOpen: true,
  mobileMenuOpen: false,
  theme: 'light',
  
  toggleSidebar: () => set((state) => ({ sidebarOpen: !state.sidebarOpen })),
  setSidebarOpen: (open) => set({ sidebarOpen: open }),
  toggleMobileMenu: () => set((state) => ({ mobileMenuOpen: !state.mobileMenuOpen })),
  setMobileMenuOpen: (open) => set({ mobileMenuOpen: open }),
  setTheme: (theme) => {
    set({ theme });
    if (theme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  },
  toggleTheme: () => set((state) => {
    const newTheme = state.theme === 'light' ? 'dark' : 'light';
    if (newTheme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    return { theme: newTheme };
  }),
}));

// Selected items store (for bulk operations)
export const useSelectionStore = create((set) => ({
  selectedUsers: [],
  selectedFahrzeuge: [],
  selectedEinteilungen: [],
  
  toggleUserSelection: (userId) => set((state) => ({
    selectedUsers: state.selectedUsers.includes(userId)
      ? state.selectedUsers.filter(id => id !== userId)
      : [...state.selectedUsers, userId]
  })),
  clearUserSelection: () => set({ selectedUsers: [] }),
  
  toggleFahrzeugSelection: (fahrzeugId) => set((state) => ({
    selectedFahrzeuge: state.selectedFahrzeuge.includes(fahrzeugId)
      ? state.selectedFahrzeuge.filter(id => id !== fahrzeugId)
      : [...state.selectedFahrzeuge, fahrzeugId]
  })),
  clearFahrzeugSelection: () => set({ selectedFahrzeuge: [] }),
  
  clearAll: () => set({ selectedUsers: [], selectedFahrzeuge: [], selectedEinteilungen: [] }),
}));
